import { useState, useEffect } from 'react';

// ICONS
import { ChevronLeft, ChevronRight } from 'lucide-react';

export default function Planner() {
    const [today] = useState(new Date());
    const [month, setMonth] = useState(today.getMonth());
    const [year, setYear] = useState(today.getFullYear());

    const daysHeader = ["LUN", "MAR", "MER", "JEU", "VEN", "SAM", "DIM"];
    
    const months = [
        "Janvier", "Février", "Mars", "Avril", "Mai", "Juin",
        "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"
    ];

    const navigateYear = (direction) => {
        setYear(prev => prev + direction);
    };

    const selectMonth = (monthIndex) => {
        setMonth(monthIndex);
    };

    const navigateMonth = (direction) => {
        setMonth(prev => {
            const newMonth = prev + direction;
            if (newMonth < 0) {
                setYear(year - 1);
                return 11;
            } else if (newMonth > 11) {
                setYear(year + 1);
                return 0;
            }
            return newMonth;
        });
    };

    const handleWheel = (e) => {
        e.preventDefault();
        if (e.deltaY > 0) {
            navigateMonth(1); // Scroll down = mois suivant
        } else {
            navigateMonth(-1); // Scroll up = mois précédent
        }
    };

    // Calculer les jours du mois - Toujours 6 semaines (42 jours) pour une hauteur fixe
    const getDaysInMonth = () => {
        const firstDay = new Date(year, month, 1);
        const lastDay = new Date(year, month + 1, 0);
        const daysInMonth = lastDay.getDate();
        const startWeekday = firstDay.getDay(); // 0=Dimanche, 1=Lundi, etc.
        
        const days = [];
        
        // Ajouter les jours du mois précédent (commencer par Lundi)
        const adjustedStartDay = startWeekday === 0 ? 6 : startWeekday - 1; // Convertir Dimanche=0 en Dimanche=6
        const prevMonth = new Date(year, month - 1, 0);
        const daysInPrevMonth = prevMonth.getDate();
        
        for (let i = adjustedStartDay - 1; i >= 0; i--) {
            days.push({
                day: daysInPrevMonth - i,
                isCurrentMonth: false,
                isPrevMonth: true
            });
        }
        
        // Ajouter les jours du mois actuel
        for (let day = 1; day <= daysInMonth; day++) {
            days.push({
                day: day,
                isCurrentMonth: true,
                isPrevMonth: false
            });
        }
        
        // Compléter pour avoir exactement 42 jours (6 semaines)
        const totalCells = days.length;
        const cellsToAdd = 42 - totalCells;
        
        for (let day = 1; day <= cellsToAdd; day++) {
            days.push({
                day: day,
                isCurrentMonth: false,
                isPrevMonth: false
            });
        }
        
        return days;
    };

    const monthDays = getDaysInMonth();

    return (
        <div>
            <h3 className="text-xl font-semibold text-gray-800 dark:text-blanc">Planificateur de thèmes</h3>
            {/* Content of the planner */}
            <div className="mt-8 flex gap-8">
                {/* Year and Month Selector */}
                <div className="w-48 px-4 flex flex-col">
                    {/* Year Selector - Tout en haut */}
                    <div className="flex items-center justify-between">
                        <button 
                            onClick={() => navigateYear(-1)}
                            className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors p-1"
                        >
                            <ChevronLeft className="size-4" />
                        </button>
                        
                        <div className="text-lg font-semibold text-gray-800 dark:text-blanc">
                            {year}
                        </div>
                        
                        <button 
                            onClick={() => navigateYear(1)}
                            className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors p-1"
                        >
                            <ChevronRight className="size-4" />
                        </button>
                    </div>

                    {/* Month Selector */}
                    <div 
                        className="cursor-pointer flex-1 flex items-top mt-10"
                        onWheel={handleWheel}
                    >
                        {/* Months List - Vertical Stack */}
                        <div className="space-y-4 text-center w-full">
                            {/* Previous month */}
                            <button 
                                onClick={() => navigateMonth(-1)}
                                className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors block w-full"
                            >
                                {months[month === 0 ? 11 : month - 1]}
                            </button>
                            
                            {/* Current month - Centered and prominent */}
                            <div className="text-xl font-bold dark:text-blanc py-2">
                                {months[month]}
                            </div>
                            
                            {/* Next month */}
                            <button 
                                onClick={() => navigateMonth(1)}
                                className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors block w-full"
                            >
                                {months[month === 11 ? 0 : month + 1]}
                            </button>
                            
                            {/* Second next month */}
                            <button 
                                onClick={() => navigateMonth(2)}
                                className="text-xs text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors block w-full"
                            >
                                {months[month === 11 ? 1 : month === 10 ? 0 : month + 2]}
                            </button>
                        </div>
                    </div>
                </div>

                {/* Calendar */}
                <div className="flex-1">
                    {/* Header of the planner */}
                    <div className="flex justify-between items-center ml-5">
                        <div className="text-lg font-regular text-gray-700 dark:text-gray-300">{months[month]} {year}</div>
                    </div>
                    {/* Body of the planner */}
                    <div className="mt-6 w-fit">
                    {/* Days of the week */}
                    <div className="grid grid-cols-7 gap-1 mb-2">
                        {daysHeader.map((day, index) => (
                            <div key={index} className="font-bold text-xs text-orange text-center w-10 mx-4">
                                {day}
                            </div>
                        ))}
                    </div>
                    {/* Days of the month */}
                    <div className="grid grid-cols-7 gap-1">
                        {monthDays.map((dayObj, index) => (
                            <div 
                                key={index} 
                                className={`text-sm text-center size-10 m-4 flex items-center justify-center rounded-lg cursor-pointer ${
                                    dayObj.isCurrentMonth 
                                        ? 'text-gray-800 dark:text-blanc hover:bg-framboise hover:text-blanc transition-colors duration-300' 
                                        : 'text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300'
                                }`}
                                onClick={() => {
                                    if (!dayObj.isCurrentMonth) {
                                        if (dayObj.isPrevMonth) {
                                            // Jour du mois précédent - aller au mois précédent
                                            navigateMonth(-1);
                                        } else {
                                            // Jour du mois suivant - aller au mois suivant
                                            navigateMonth(1);
                                        }
                                    }
                                }}
                            >
                                {dayObj.day}
                            </div>
                        ))}
                    </div>
                    </div>
                </div>
            </div>
        </div>
    );
}